import { TransportType } from ".";

import { timeZoneMap } from "@web-app/utils/helpers";

import { App } from "./apps";
import { Attachment } from "./attachments";
import { CustomField, CustomFieldValue } from "./customFields";
import { ExternalServiceAssignee } from "./externalServiceAssignee";
import { GoogleAddress } from "./google";
import { NotificationEvent } from "./notificationEvents";
import { OrderTimeline } from "./orderTimelines";
import { Route } from "./routes";
import { User } from "./users";
import { Workspace } from "./workspaces";

export enum OrderPriorityType {
  HIGH = "HIGH",
  LOW = "LOW",
}

export type LocationDetails = {
  address: string;
  adminArea2: string;
  businessName: string;
  country: string;
  extendedAddress: string;
  locality: string;
  name: string;
  note: string;
  phone: string;
  postalCode: string;
  state: string;
  stateId: string;
  streetAddress: string;
};

export type ETA = {
  dateTime: Date | null;
  lastUpdated: Date | null;
  originalDistance: number;
  progress: number;
  updateCount: number;
};

export enum OrderDeliveryType {
  ROUTE_OPT_PICKUP = "ROUTE_OPT_PICKUP",
  ROUTE_OPT_DROPOFF = "ROUTE_OPT_DROPOFF",
  INDIVIDUAL_PICKUP_AND_DROPOFF = "INDIVIDUAL_PICKUP_AND_DROPOFF",
}

export type OrderRequirements = {
  itemChecklist?: boolean;
  note: boolean;
  photo: boolean;
  signature: boolean;
};

export type ChecklistItem = {
  isChecked: boolean;
  isMandatory: boolean;
  label: string;
  quantity: number;
};

export enum OrderStatus {
  TODO = "TODO",
  ASSIGNED = "ASSIGNED",
  INPROGRESS = "INPROGRESS",
  COMPLETE = "COMPLETE",
  FAILED = "FAILED",
  CANCELLED = "CANCELLED",
}

export enum OrderStatusProgress {
  PICKUP = "PICKUP",
  DROPOFF = "DROPOFF",
}

export type OrderDetails = {
  currency: "USD";
  description: string;
  distanceString: string;
  height: number;
  itemsCount: number;
  length: number;
  serviceDuration: number;
  tip: number;
  value: number;
  weight: number;
  width: number;
};

export enum TransportTypeV2 {
  Bicycle = "CYCLE",
  "Box Truck" = "TRUCK",
  "E-bike" = "EBIKE",
  "E-scooter" = "ESCOOTER",
  Motorcycle = "BIKE",
  Other = "OTHER",
  "Pickup Truck" = "PICK_UP",
  Sedan = "SEDAN",
  "Station Wagon" = "ESTATE",
  SUV = "SUV",
  "Sprinter Van" = "VAN",
  Walking = "WALK",
}

export type OrderStatusText =
  | ""
  | "Assigned"
  | "Cancelled"
  | "Completed"
  | "Failed"
  | "In Transit to Drop-off"
  | "In Transit to Pickup"
  | "In Transit"
  | "Unassigned";

export type OrderDeliveryTypeText =
  "Individual" | "None" | "Route based drop-off" | "Route based pickup";

export type OrderCustomField = {
  createdAt: string;
  customField: CustomField;
  customFieldId: string;
  customFieldValues: CustomFieldValue[];
  groupId: string;
  id: string;
  orderId: string;
  updatedAt: string;
  workspaceId: string;
};

export type UpdateOrderPayload = {
  actualEndDateTime: Date | null;
  actualPickupDateTime: Date | null;
  actualStartDateTime: Date | null;
  app: App;
  assigneeId: string | null;
  attachments: Attachment[];
  connectedWorkspace: Workspace | null;
  createdBy: User;
  customFields: OrderCustomField[];
  deliveryPlaceId?: string | null;
  deliveryTypeText: OrderDeliveryTypeText;
  details: OrderDetails;
  displayId: string;
  dropoff: LocationDetails;
  dropoffChecklist: Array<ChecklistItem>;
  dropoffLocation: {
    coordinates: number[];
    type: string;
  };
  dropoffLocationLat: number;
  dropoffLocationLng: number;
  dropoffRequirements: OrderRequirements;
  eta: ETA;
  etaOrigin: string;
  externalAssigneeId: string | null;
  externalId: string;
  geoDropoffDateTime: Date | null;
  geoPickupDateTime: Date | null;
  id: string;
  minVehicle: TransportType;
  notificationEvents: NotificationEvent[];
  pickup: LocationDetails;
  pickupChecklist: Array<ChecklistItem>;
  pickupLocation: {
    coordinates: number[];
    type: string;
  };
  pickupLocationLat: number;
  pickupLocationLng: number;
  pickupPlaceId?: string | null;
  pickupRequirements: OrderRequirements;
  priority: OrderPriorityType;
  referenceCount: number;
  /**
   * @deprecated use routeId instead
   */
  route: Route | null;
  scheduledEndDateTime: Date;
  scheduledStartDateTime: Date;
  sourceId: string | null;
  status: OrderStatus;
  statusDetails: { note: string; reason: string };
  statusProgress: OrderStatusProgress | null;
  statusText: OrderStatusText;
  timelineEvents: OrderTimeline[];
  trackingUrl: string;
  type: OrderDeliveryType;
  workspace: Workspace;
};

export type Order = {
  actualEndDateTime: Date | null;
  actualPickupDateTime: Date | null;
  actualStartDateTime: Date | null;
  appId: string | null;
  assigneeId: string;
  attachments: Attachment[];
  connectedWorkspaceId: string;
  createdAt: Date;
  customFields: {
    definition: CustomField;
    values: CustomFieldValue[];
  }[];
  deliveryPlaceId?: string | null;
  deliveryTypeText: OrderDeliveryTypeText;
  details: OrderDetails;
  displayId: string;
  driverRouteDistance: number | null;
  dropoff: LocationDetails;
  dropoffChecklist: Array<ChecklistItem>;
  dropoffLocation: {
    coordinates: number[];
    type: string;
  };
  dropoffRequirements: OrderRequirements;
  estimatedDistance: number | null;
  estimatedEtaDateTime: Date | null;
  eta: ETA;
  etaOrigin: string;
  externalAssignee: ExternalServiceAssignee;
  externalAssigneeId: string | null;
  externalId: string;
  geoDropoffDateTime: Date | null;
  geoPickupDateTime: Date | null;
  id: string;
  minVehicle: TransportType;
  pickup: LocationDetails;
  pickupAddress: string;
  pickupChecklist: Array<ChecklistItem>;
  pickupEta: ETA | null;
  pickupLocation: {
    coordinates: number[];
    type: string;
  };
  pickupPlaceId?: string | null;
  pickupRequirements: OrderRequirements;
  priority: OrderPriorityType;
  referenceCount: number;
  reports: Report[];
  /**
   * @deprecated use routeId instead
   */
  route: Route | null;
  routeId: string | null;
  scheduledEndDateTime: Date;
  scheduledStartDateTime: Date;
  sourceId: string | null;
  status: OrderStatus;
  statusDetails: { note: string; reason: string };
  statusProgress: OrderStatusProgress | null;
  statusText: OrderStatusText;
  timelineEvents: OrderTimeline[];
  timezone?: keyof typeof timeZoneMap;
  trackingUrl: string;
  triggeringUserId: string;
  type: OrderDeliveryType;
  updatedAt: Date;
  workspaceId: string;
};

export type CreateBulkOrdersPayload = {
  connectedWorkspaceId: string | null;
  importType: "INDIVIDUAL" | "ROUTE";
  url: string;
  workspaceId: string;
};

export type UpdateBulkOrdersPayload = {
  orderIds: string[];
  updatedValues: Partial<Order>;
};

export type DeleteBulkOrdersPayload = {
  orderIds: string[];
};

export type DriverRouteType = {
  distance: number;
  locationEntries: {
    coordinates: {
      latitude: number;
      longitude: number;
    };
    timestamp: string;
  }[];
};

export type FieldValue =
  | { definition: CustomField; values: CustomFieldValue[] }[]
  | ChecklistItem[]
  | GoogleAddress
  | boolean
  | number
  | string
  | null;
